<template>
  <section class="simple-comparison-section">
    <h2 class="rr_section_title simple-comparison__title">
      {{ title || $t('product.comparison.comparison_to_similar', { category: (currentCategory.name || '').toLowerCase() }) }}
    </h2>
    <p v-if="titleText" class="simple-comparison__title-text" v-text="titleText"></p>
    <table class="simple-comparison">
      <tbody>
      <component
        :is="rowComponent(row.type)"
        v-for="(row, index) in rowsFiltered"
        :key="index"
        :data="row"
        :number-products="numberProducts"
        :index="index"
        :no-highlight="noHighlight"
      />
      </tbody>
    </table>
  </section>
</template>

<script setup>
import { nextTick } from "vue";

import SimpleComparisonRow from '@/components/shared/simple-comparison/SimpleComparisonRow';
import SimpleComparisonRowArray from '@/components/shared/simple-comparison/SimpleComparisonRowArray';
import SimpleComparisonImagesRow from '@/components/shared/simple-comparison/SimpleComparisonImagesRow';
import SimpleComparisonCoreScoreRow from '@/components/shared/simple-comparison/SimpleComparisonCoreScoreRow';
import SimpleComparisonExpertReviewsRow from '@/components/shared/simple-comparison/SimpleComparisonExpertReviewsRow';
import SimpleComparisonUsersRatingRow from '@/components/shared/simple-comparison/SimpleComparisonUsersRatingRow';
import SimpleComparisonRowRank from '@/components/shared/simple-comparison/SimpleComparisonRowRank';
import SimpleComparisonPriceRow from '@/components/shared/simple-comparison/SimpleComparisonPriceRow';
import SimpleComparisonBadgeRow from '@/components/shared/simple-comparison/SimpleComparisonBadgeRow';

import SyncScroll from '~/utils/syncScroll';
import { useSimpleComparisonStore } from "~/stores/simpleComparison";
import { useMainStore } from "~/stores/main";
import { useCategoryGroupsStore } from "~/stores/categoryGroups";

const ROWS_TYPES = {
  images: SimpleComparisonImagesRow,
  score: SimpleComparisonCoreScoreRow,
  expertReviews: SimpleComparisonExpertReviewsRow,
  usersRating: SimpleComparisonUsersRatingRow,
  normal: SimpleComparisonRow,
  array: SimpleComparisonRowArray,
  price: SimpleComparisonPriceRow,
  rank: SimpleComparisonRowRank,
  'bordered-text': SimpleComparisonBadgeRow,
};

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  titleText: {
    type: String,
    default: '',
  },
  currentProduct: {
    type: Object,
    required: true,
  },
  numberProducts: {
    type: Number,
    default: 5,
  },
  numberProductsMobile: {
    type: Number,
    default: 2,
  },
  currentCategory: {
    type: Object,
    required: true,
  },
  scoresSmall: {
    type: Boolean,
    default: false,
  },
  sameCollection: {
    type: Boolean,
    default: false,
  },
  forceProducts: {
    type: Array,
    default() {
      return [];
    }
  },
  forceColor: {
    type: String,
    default: ''
  },
  noHighlight: {
    type: Boolean,
    default: false
  },
  labels: {
    type: Object,
    default() {
      return {};
    }
  }
})

const { t } = useI18n();
const { isMobileOrTablet } = useDevice();

const simpleComparison = useSimpleComparisonStore();
const main = useMainStore();
const categoryGroups = useCategoryGroupsStore();

const rowsFiltered = computed(() => {
  if (simpleComparison.rowsData.length === 0) {
    return [];
  }

  let output = simpleComparison.rowsData.filter(
    row => row.type !== 'expertReviews' || categoryGroups.currentCategory.slug !== 'sneakers',
  );

  if (!props.labels || Object.keys(props.labels).length === 0) {
    return output;
  }

  let refColumns = output[0].columns;
  let outputColumns = [];
  for (let i in refColumns) {
    outputColumns.push({
      value: props.labels[refColumns[i].id] ?? ""
    });
  }

  output.splice(1, 0, {
    columns: outputColumns,
    label: t('generic.best'),
    type: 'bordered-text'
  });

  return output;
})

// Fetch data only on client!!!
onMounted(() => {
  // We need 1 empty column with `Add` button
  simpleComparison.numberOfProductsToCompare = props.numberProducts - 1;

  if (simpleComparison.products.length === 0) {
    fetch();
  }
})

watch(() => main.fullParamsHash, (value) => {
  fetch();
});

watch(() => simpleComparison.loadingSimilarProducts, (value) => {
  if (isMobileOrTablet) {
    syncScroll();
  }
})

watch(simpleComparison.products, (value) => {
  if (!value && isMobileOrTablet) {
    syncScroll();
  }
})

function rowComponent(type) {
  return ROWS_TYPES?.[type] || ROWS_TYPES.normal;
}

function syncScroll() {
  if (process.server) {
    return;
  }
  nextTick(() => setTimeout(() => SyncScroll.onMobile()));
}
function fetch() {
  simpleComparison.fetchInitial({
    productId: props.currentProduct.id,
    sameCollection: props.sameCollection,
    forceProducts: props.forceProducts,
    forceColor: props.forceColor,
  });
}
</script>

<style lang="scss">
.simple-comparison-section {
  position: relative;
  font-size: 1.4rem;
  .loading-placeholder {
    height: 400px;
    @extend %loading-placehodler;
  }
  .simple-comparison {
    width: 100%;
    display: inline-block;
    position: relative;
    margin-bottom: 4px;

    @media(max-width: $break_md_under) {
      overflow-x: scroll;
    }

    &.expanded {
      max-height: none;
      padding-bottom: 20px;
    }
    .btn-expand-container {
      position: absolute;
      bottom: 0;
      width: 100%;
      background: #fff;
      border-radius: 50px 0;
      .btn-expand {
        width: 100%;
        text-align: center;
        font-size: 22px;
        font-weight: 200;
        border: 2px solid $theme-color;
        border-radius: 50px;
        color: $theme-color;
        &:hover,
        &:active {
          background-color: $theme-color;
          color: white;
        }
        .arrow {
          @extend %arrow;
        }
      }
    }
    .simple-comparison-row {
      .simple-comparison-row__label {
        border-left: 0.09rem solid #ccc;
        border-bottom: 0.09rem solid #ccc;
        border-right: 0.09rem solid $black;
        &.no-border {
          border-right: 0.09rem solid #ccc;
        }
      }

      .similar-comparison-data {
        border-right: 0.09rem solid #ccc;

        &.similar-comparison-data-first {
          border-right-color: $black;
        }

        &:not(.simple-comaprison-data__empty-slot) {
          border-bottom: 0.09rem solid #ccc;
        }

        &:last-of-type {
          border-right: 0.09rem solid #ccc;
        }
      }

      &:first-of-type {
        background: white;

        .even {
          background: none;
        }

        .simple-comparison-row__label,
        .similar-comparison-data {
          border-top: 0.09rem solid #ccc;
        }

        .similar-comparison-data {
          border-right-color: transparent;

          &:last-of-type {
            border-right-color: #ccc;
          }

          &.similar-comparison-data-first {
            border-top-color: $black;
            border-right-color: $black;
          }
        }
      }

      &:last-of-type {
        .similar-comparison-data.simple-comaprison-data__empty-slot {
          border-bottom: 0.09rem solid #ccc;
        }
        .similar-comparison-data.similar-comparison-data-first {
          border-bottom-color: $black;
        }
      }

      @media (max-width: $break_xs_under) {
        width: 100%;
      }
    }
  }
  .simple-comparison__title-text {
    font-size: calc(1.4rem + 2px);
  }
}
</style>
